import { Log, LogCategory, Operation as LogOperation } from '@augloop/logging';
import { SessionLongPollMessage, SessionLongPollMessageType, SessionLongPollResponse } from '@augloop/session-protocol';
const longPollIntervalMs = 15 * 1000;
const sendLongPollDelayMs = longPollIntervalMs + 5 * 1000;
const stopLongPollingTimeMs = 60 * 1000;
const timeoutErrorString = 'Request timed out';
// Max number of retry attempts for heartbeat
const longPollRetryAttemptMax = 4;
export class HttpReliabilityManager {
    constructor(worker, clientMetadata, sessionCorrelationVector) {
        this.remainingRetryAttempts = longPollRetryAttemptMax;
        this.isAsleep = false;
        this.isLongPollSuccessful = false;
        this.isActiveLongPoll = false;
        this.isResponseReceived = false;
        this.lastEgressTime = 0;
        this.longPollLogOp = new LogOperation({ operationName: 'OnLongPollMessage', success: true });
        this.clientMetadata = clientMetadata;
        this.sessionCorrelationVector = sessionCorrelationVector;
        this.longPollLogOp.setClientMetadata(this.clientMetadata, true /*skipFlights*/);
        this.worker = worker;
    }
    start() {
        const startManagerLogOp = new LogOperation({ operationName: 'StartHttpReliabilityManager', success: true }).start();
        startManagerLogOp.setClientMetadata(this.clientMetadata, true /*skipFlights*/);
        Log.info(0x1e49f547 /* tag_4s5vh */, LogCategory.CoreDefault, startManagerLogOp.stop());
        this.trySendLongPoll(SessionLongPollMessageType.Start, true /* awaken */);
        this.isLongPollSuccessful = true;
    }
    onResponse(response) {
        if (!response || this.isResponseReceived) {
            const httpManagerFailure = new LogOperation({ operationName: 'HttpReliabilityManagerFailure', success: false }).start();
            httpManagerFailure.setClientMetadata(this.clientMetadata, true /*skipFlights*/);
            Log.info(0x1e49f1c1 /* tag_4s5hb */, LogCategory.CoreDefault, httpManagerFailure.stop());
            return;
        }
        this.isResponseReceived = true;
        this.isActiveLongPoll = false;
        const longPollResponse = response;
        if (longPollResponse.error) {
            this.isLongPollSuccessful = false;
            --this.remainingRetryAttempts;
            if (this.remainingRetryAttempts > 0) {
                this.longPollLogOp.success = false;
                this.longPollLogOp.resultDescription = `Retry attempts left : ${this.remainingRetryAttempts}`;
                this.longPollLogOp.resultSignature = longPollResponse.error;
                Log.info(0x1e49f546 /* tag_4s5vg */, LogCategory.CoreDefault, this.longPollLogOp.stop());
                const sessionLongPollMessageType = (longPollResponse.error === timeoutErrorString) ? SessionLongPollMessageType.TimeoutResend : SessionLongPollMessageType.FailResend;
                if (longPollResponse.error === timeoutErrorString || this.remainingRetryAttempts === longPollRetryAttemptMax - 1) {
                    this.trySendLongPoll(sessionLongPollMessageType, true /* awaken */);
                }
                else {
                    // Delay long poll retry if we get a non-timeout error
                    // TODO: 8234108 - Exponential backoff for long poll retries. We must try immediately if the failure was intermittent the first time
                    this.enqueueSendLongPoll(sessionLongPollMessageType);
                }
            }
            else {
                this.longPollLogOp.success = false;
                this.longPollLogOp.resultDescription = `Long poll still not received after all retry attempts`;
                Log.info(0x1e49f545 /* tag_4s5vf */, LogCategory.CoreDefault, this.longPollLogOp.stop());
                // Stop pinging and disconnect
                if (this.worker) {
                    this.worker.close();
                }
            }
        }
        else {
            // Reset retry attempts and send another LP message
            this.longPollLogOp.success = true;
            this.longPollLogOp.resultDescription = 'Long poll received';
            Log.info(0x1e49f549 /* tag_4s5vj */, LogCategory.CoreDefault, this.longPollLogOp.stop());
            this.remainingRetryAttempts = longPollRetryAttemptMax;
            this.isLongPollSuccessful = true;
            this.trySendLongPoll(SessionLongPollMessageType.Regular);
        }
    }
    close() {
        const closeManagerLogOp = new LogOperation({ operationName: 'CloseHttpReliabilityManager', success: true }).start();
        closeManagerLogOp.setClientMetadata(this.clientMetadata, true /*skipFlights*/);
        Log.info(0x1e49f1a3 /* tag_4s5g9 */, LogCategory.CoreDefault, closeManagerLogOp.stop());
        if (this.sendLongPollTimer) {
            clearTimeout(this.sendLongPollTimer);
            this.sendLongPollTimer = undefined;
        }
        this.isActiveLongPoll = false;
        this.isLongPollSuccessful = false;
        this.worker = undefined;
    }
    checkConnection() {
        if (this.isAsleep) {
            this.isAsleep = false;
            const awakenManagerLogOp = new LogOperation({ operationName: 'AwakenHttpReliabilityManager', success: true }).start();
            awakenManagerLogOp.setClientMetadata(this.clientMetadata, true /*skipFlights*/);
            Log.info(0x1e49f1c0 /* tag_4s5ha */, LogCategory.CoreDefault, awakenManagerLogOp.stop());
            this.trySendLongPoll(SessionLongPollMessageType.CheckConnection, true /* awaken */);
        }
        return this.isLongPollSuccessful;
    }
    isReliabilityResponse(response) {
        return typeof response !== 'string' && SessionLongPollResponse.typeGuard(response);
    }
    postEgress() {
        // Must send a long poll before egress
        this.lastEgressTime = Date.now();
        this.trySendLongPoll(SessionLongPollMessageType.PostEgress);
    }
    needsParsedResponses() {
        return true;
    }
    enqueueSendLongPoll(type) {
        this.sendLongPollTimer = setTimeout(() => {
            this.trySendLongPoll(type, true /* awaken */);
        }, sendLongPollDelayMs);
    }
    trySendLongPoll(type, awaken) {
        if (!this.worker) {
            const longPollNoOp = new LogOperation({ operationName: 'LongPollNoOp', success: true }).start();
            longPollNoOp.setClientMetadata(this.clientMetadata, true /*skipFlights*/);
            longPollNoOp.resultDescription = 'Worker is undefined';
            Log.info(0x1e3c801e /* tag_4pia4 */, LogCategory.CoreDefault, longPollNoOp.stop());
            return;
        }
        if (this.isActiveLongPoll === true) {
            const longPollNoOp = new LogOperation({ operationName: 'LongPollNoOp', success: true }).start();
            longPollNoOp.setClientMetadata(this.clientMetadata, true /*skipFlights*/);
            longPollNoOp.resultDescription = 'Already active long poll';
            Log.info(0x1e49f544 /* tag_4s5ve */, LogCategory.CoreDefault, longPollNoOp.stop());
            return;
        }
        // Set it immediately in case of a context switch
        this.isActiveLongPoll = true;
        if (Date.now() - this.lastEgressTime > stopLongPollingTimeMs && !awaken) {
            // If we haven't sent any messages in 1 min, it's safe to stop long polling until we egress again
            this.isActiveLongPoll = false;
            this.isAsleep = true;
            const longPollSleep = new LogOperation({ operationName: 'LongPollSleep', success: true }).start();
            longPollSleep.setClientMetadata(this.clientMetadata);
            longPollSleep.resultDescription = `isAsleep: ${this.isAsleep}, isActiveLongPoll: ${this.isActiveLongPoll}`;
            Log.info(0x1e3c7593 /* tag_4phwt */, LogCategory.CoreDefault, longPollSleep.stop());
            return;
        }
        this.longPollLogOp.start();
        this.isResponseReceived = false;
        this.worker.egress({ obj: this.getLongPollMessageString(type), isHttpSessionLongPollMessage: true, isHttpSessionInitMessage: false });
    }
    getLongPollMessageString(type) {
        const message = new SessionLongPollMessage({ longPollTimeoutHint: longPollIntervalMs, type: type });
        if (this.sessionCorrelationVector) {
            message.cv = this.sessionCorrelationVector().newChild().toString();
        }
        return JSON.stringify(message);
    }
}
//# sourceMappingURL=http-reliability-manager.js.map